import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ArrowTrendingUpIcon, ScaleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { analyticsApi } from '@/services/api';
import PerformanceChart from '@/components/Analytics/PerformanceChart';
import RiskMetricsCard from '@/components/Analytics/RiskMetricsCard';
import AttributionBreakdown from '@/components/Analytics/AttributionBreakdown';
import BenchmarkComparison from '@/components/Analytics/BenchmarkComparison';
import CorrelationMatrix from '@/components/Analytics/CorrelationMatrix';
import RiskVisualizationSuite from '@/components/Analytics/RiskVisualizationSuite';
import CandlestickChart from '@/components/Charts/CandlestickChart';

interface PerformanceData {
  performance: {
    period: number;
    totalReturn: number;
    totalReturnUSD: number;
    annualizedReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    volatility: number;
    winRate: number;
    totalTrades: number;
    avgTradeReturn: number;
    bestDay: number;
    worstDay: number;
    marketComparison: {
      btcReturn: number;
      ethReturn: number;
      sp500Return: number;
      outperformance: number;
    } | null;
  };
  breakdown: {
    byAsset: Array<{
      symbol: string;
      return: number;
      allocation: number;
      contribution: number;
    }>;
    byStrategy: Array<{
      strategyName: string;
      return: number;
      trades: number;
      contribution: number;
    }>;
  };
}

interface RiskData {
  riskMetrics: {
    portfolioVaR95: number;
    portfolioVaR99: number;
    expectedShortfall: number;
    beta: number;
    correlation: Record<string, number>;
    concentrationRisk: {
      maxAssetWeight: number;
      herfindahlIndex: number;
      diversificationRatio: number;
    };
  };
  recommendations: string[];
  correlationMatrix?: Record<string, number>;
  correlationSummary?: Record<string, Record<string, number>>;
}

interface BenchmarkData {
  benchmarks: {
    comparison: {
      btcReturn: number;
      ethReturn: number;
      sp500Return: number;
      outperformance: number;
    };
    benchmarkReturns: Record<string, number>;
    availableBenchmarks: string[];
  };
}

const AnalyticsPage: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [riskData, setRiskData] = useState<RiskData | null>(null);
  const [benchmarkData, setBenchmarkData] = useState<BenchmarkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [refreshing, setRefreshing] = useState(false);
  const [tradingMode, setTradingMode] = useState<'paper' | 'live'>('paper');

  const loadAnalyticsData = async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Load all analytics data in parallel with trading mode
      const [performanceResponse, riskResponse, benchmarkResponse] = await Promise.all([
        analyticsApi.getPerformanceAnalytics({ 
          period: selectedPeriod, 
          compareToMarket: true, 
          includeAttribution: true,
          tradingMode: tradingMode
        }),
        analyticsApi.getRiskMetrics({ 
          period: selectedPeriod, 
          includeCorrelations: true,
          tradingMode: tradingMode
        }),
        analyticsApi.getBenchmarkComparisons({ 
          period: selectedPeriod, 
          benchmarks: ['BTC', 'ETH', 'SPY'],
          tradingMode: tradingMode
        })
      ]);

      // Handle API response structure - backend wraps responses in { data: { ... } }
      const performanceResult: any = performanceResponse;
      const riskResult: any = riskResponse;
      const benchmarkResult: any = benchmarkResponse;
      
      // Extract data from nested structure
      setPerformanceData(performanceResult.data || performanceResult);
      setRiskData(riskResult.data || riskResult);
      setBenchmarkData(benchmarkResult.data || benchmarkResult);

    } catch (err: any) {
      console.error('Failed to load analytics data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod, tradingMode]);

  const handleRefresh = () => {
    loadAnalyticsData(true);
  };

  const handlePeriodChange = (period: number) => {
    setSelectedPeriod(period);
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading advanced analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Analytics Error</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <div className="mt-6">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if there's no data available
  const hasNoData = (performanceData as any)?._noData || 
                    (benchmarkData as any)?._noData || 
                    (riskData as any)?._noData;
  
  const noDataMessage = (performanceData as any)?._message || 
                        (benchmarkData as any)?._message || 
                        (riskData as any)?._message ||
                        `No ${tradingMode === 'paper' ? 'paper trading' : 'live trading'} data available. Start trading to see analytics.`;

  return (
    <div className="min-h-screen bg-secondary-custom">
      {hasNoData && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="text-yellow-400">ℹ️</div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <strong>No Data Available:</strong> {noDataMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-heading-1 font-bold leading-7 text-primary-custom sm:text-3xl sm:truncate">
              Advanced Analytics
            </h2>
            <p className="mt-1 text-body text-secondary-custom">
              Comprehensive performance and risk analysis for your trading portfolio
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            {/* Trading Mode Toggle */}
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setTradingMode('paper')}
                className={`px-4 py-2 text-sm font-medium border ${
                  tradingMode === 'paper'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'card text-secondary-custom border-secondary-custom hover-bg-secondary-custom'
                } rounded-l-md`}
              >
                Paper Trading
              </button>
              <button
                onClick={() => setTradingMode('live')}
                className={`px-4 py-2 text-sm font-medium border border-l-0 ${
                  tradingMode === 'live'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'card text-secondary-custom border-secondary-custom hover-bg-secondary-custom'
                } rounded-r-md`}
              >
                Live Trading
              </button>
            </div>

            {/* Period Selector */}
            <div className="flex rounded-md shadow-sm">
              {[7, 30, 90, 180].map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodChange(period)}
                  className={`px-4 py-2 text-body font-medium border ${
                    selectedPeriod === period
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'card text-secondary-custom border-secondary-custom hover-bg-secondary-custom'
                  } ${period === 7 ? 'rounded-l-md' : ''} ${period === 180 ? 'rounded-r-md border-l-0' : 'border-l-0'}`}
                >
                  {period}d
                </button>
              ))}
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-secondary-custom shadow-sm text-body font-medium rounded-md text-secondary-custom card hover-bg-secondary-custom disabled:opacity-50"
            >
              <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Performance Overview Cards */}
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-body font-medium text-secondary-custom truncate">Total Return</dt>
                    <dd className="text-heading-3 font-medium text-primary-custom">
                      {performanceData?.performance.totalReturn.toFixed(2)}%
                      <span className="text-body text-secondary-custom ml-2">
                        (${performanceData?.performance.totalReturnUSD.toFixed(2)})
                      </span>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ScaleIcon className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-body font-medium text-secondary-custom truncate">Sharpe Ratio</dt>
                    <dd className="text-heading-3 font-medium text-primary-custom">
                      {performanceData?.performance.sharpeRatio.toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="card overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-body font-medium text-secondary-custom truncate">Max Drawdown</dt>
                    <dd className="text-heading-3 font-medium text-primary-custom">
                      -{performanceData?.performance.maxDrawdown.toFixed(2)}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Volatility</dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {performanceData?.performance.volatility.toFixed(2)}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Analytics Content */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Performance Chart */}
          <div className="col-span-1 lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Performance Overview
              </h3>
              <PerformanceChart 
                performanceData={performanceData?.performance} 
                period={selectedPeriod}
              />
            </div>
          </div>

          {/* Risk Metrics */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Risk Analysis
            </h3>
            <RiskMetricsCard 
              riskData={riskData?.riskMetrics}
              recommendations={riskData?.recommendations || []}
            />
          </div>

          {/* Benchmark Comparison */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Benchmark Comparison
            </h3>
            <BenchmarkComparison 
              benchmarkData={benchmarkData?.benchmarks}
              portfolioReturn={performanceData?.performance.totalReturn || 0}
            />
          </div>

          {/* Attribution Analysis */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Attribution Analysis
            </h3>
            <AttributionBreakdown 
              breakdown={performanceData?.breakdown}
            />
          </div>

          {/* Correlation Matrix */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Asset Correlation Analysis
            </h3>
            <CorrelationMatrix 
              correlationData={riskData?.riskMetrics?.correlation ? 
                // Transform flat correlation data to nested format expected by CorrelationMatrix
                Object.keys(riskData.riskMetrics.correlation).reduce((acc, key) => {
                  acc[key] = riskData.riskMetrics!.correlation;
                  return acc;
                }, {} as Record<string, Record<string, number>>) : 
                undefined
              }
            />
          </div>
        </div>

        {/* Advanced Risk Visualization Suite */}
        <div className="mt-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-6">
              Advanced Risk Analytics
            </h3>
            <RiskVisualizationSuite 
              riskData={riskData?.riskMetrics}
              performanceData={performanceData?.performance}
              period={selectedPeriod}
            />
          </div>
        </div>

        {/* Candlestick Chart Section */}
        <div className="mt-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-6">
              Price Analysis
            </h3>
            <CandlestickChart 
              symbol="BTC/USD"
              height={450}
              showVolume={true}
              timeframe="1D"
            />
          </div>
        </div>

        {/* Trading Statistics */}
        <div className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-6">
            Trading Performance
          </h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceData?.performance.totalTrades || 0}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Trades</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {performanceData?.performance.winRate.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Win Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {performanceData?.performance.avgTradeReturn.toFixed(2)}%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Avg Trade Return</div>
            </div>
          </div>
        </div>

        {/* Risk Recommendations */}
        {riskData?.recommendations && riskData.recommendations.length > 0 && (
          <div className="mt-8 bg-warning-subtle border border-warning-subtle rounded-lg p-6">
            <h3 className="text-heading-2 leading-6 font-medium text-warning-custom mb-4">
              Risk Management Recommendations
            </h3>
            <ul className="space-y-2">
              {riskData.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-warning-custom">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage; 